import { t } from '@lingui/core/macro';
import { ClickAwayListener, styled } from '@mui/material';
import { SearchIcon, XIcon } from 'lucide-react';
import React, { type JSX } from 'react';

import { useSearch, useSetSearch } from '../../routing';
import { colorSystem, INTER } from '../../theme';
import { TertiaryIconButton } from '../buttons';
import { MenuFields } from './components/MenuFields';
import { MenuOperatorArgument } from './components/MenuOperatorArgument';
import { MenuOperators } from './components/MenuOperators';
import { ParametricInputTooltip } from './components/ParametricInputTooltip';
import { SearchBlockView as DefaultSearchBlockView } from './components/SearchBlockView';
import { reducer } from './reducer';
import { findAllowedParameters } from './selectors/findAllowedParameters';
import { Actions, AutocompleteOptions, ParametricSearchState } from './types';
import { parseInitialSelectedBlocks } from './util/parseInitialSelectedBlocks';
import { serialiseSearchBlocks } from './util/serialiseSearchBlocks';

export interface ParametricSearchInputProps<T, TAutocompleteState = T[]> {
    placeholder?: string;
    autoFocus?: boolean;
    state: ParametricSearchState<T, TAutocompleteState>;
    dispatch: React.Dispatch<Actions<T, TAutocompleteState>>;
    overrides?: {
        Container?: typeof DefaultContainer;
        Input?: typeof DefaultInput;
        SearchBlockView?: typeof DefaultSearchBlockView;
        IconButton?: typeof TertiaryIconButton;
    };
    autocompleteState?: TAutocompleteState;
}

export interface UseParametricSearchInputStateReturn<T, TAutocompleteState> {
    state: ParametricSearchState<T, TAutocompleteState>;
    dispatch: React.Dispatch<Actions<T, TAutocompleteState>>;
}

/** @deprecated We don't recommend the parametric search anymore. */
export function useParametricSearchInputState<T, TAutocompleteState>(
    config: AutocompleteOptions<T, TAutocompleteState>,
): UseParametricSearchInputStateReturn<T, TAutocompleteState> {
    const ref = React.useRef<HTMLInputElement>(null);
    // With `history`, the selected blocks are in that query param, so a reload or a shared link restores them. The
    // config names it only at runtime, so the search is read as a record.
    const queryParameter = config.history?.queryParameter;
    const param = useSearch({
        strict: false,
        select: (search: Record<string, unknown>) => {
            const value = queryParameter === undefined ? undefined : search[queryParameter];
            return typeof value === 'string' ? value : undefined;
        },
    });
    const setSearch = useSetSearch();

    const [state, setState] = React.useState<ParametricSearchState<T, TAutocompleteState>>(() => ({
        inputRef: ref,
        config,
        query: '',
        selectedBlocks: parseInitialSelectedBlocks(config, param),
    }));
    // The state the last action left, so that each of several actions in one handler reduces the one before.
    const latest = React.useRef(state);

    // Reduced here, not in a `useReducer` (which reduces in the next render), so the handler that dispatches a change
    // to the blocks writes them. The write navigates: dispatch from event handlers. An empty selection drops the param.
    const dispatch = React.useCallback(
        (action: Actions<T, TAutocompleteState>) => {
            const previous = latest.current;
            const next = reducer(previous, action);
            latest.current = next;
            setState(next);

            const serialised = serialiseSearchBlocks({ searchBlocks: next.selectedBlocks });
            if (
                queryParameter !== undefined &&
                serialised !== serialiseSearchBlocks({ searchBlocks: previous.selectedBlocks })
            ) {
                setSearch({ [queryParameter]: serialised || undefined });
            }
        },
        [queryParameter, setSearch],
    );

    return { state, dispatch };
}

export function ParametricSearchInput<T, TAutocompleteState>({
    placeholder = '',
    autoFocus,
    state,
    dispatch,
    overrides = {},
    autocompleteState,
}: ParametricSearchInputProps<T, TAutocompleteState>): JSX.Element {
    const {
        Input = DefaultInput,
        Container = DefaultContainer,
        SearchBlockView = DefaultSearchBlockView,
        IconButton = TertiaryIconButton,
    } = overrides;

    const [showMenu, setShowMenu] = React.useState(false);

    function handleKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key === 'Escape') {
            dispatch({ type: 'removePartialFilter' });

            if (state.query.length === 0) {
                state.inputRef.current?.blur();
            }
        } else if (event.key === 'Enter') {
            dispatch({ type: 'submitQuery' });
            setShowMenu(false);
        }
    }

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
        const newQuery = event.target.value;

        dispatch({ type: 'onQueryChange', query: newQuery });
        setShowMenu(true);
    }

    const { selectedBlocks } = state;
    const blocks = selectedBlocks.map((filter, i) => {
        const parameter = state.config.parameters.find((parameter) => parameter.field === filter.field);
        return (
            <SearchBlockView
                key={i}
                block={filter}
                parameter={parameter}
                dispatch={dispatch}
                autocompleteState={autocompleteState}
            />
        );
    });

    const tooltip =
        showMenu || Boolean(state.partialBlock)
            ? findMenu({
                  state,
                  dispatch,
                  autocompleteState,
                  setShowMenu,
              })
            : '';

    const showClearIcon = state.partialBlock || state.selectedBlocks.length > 0;

    const getPlaceholder = () => {
        const { partialBlock, selectedBlocks } = state;
        if (partialBlock && partialBlock.selectedOp) {
            const { selectedOp } = partialBlock;
            switch (selectedOp.op) {
                case '<':
                case '=':
                case '>':
                case '≤':
                case '≥':
                case 'like':
                case 'starts-with':
                    return '';
                case 'is':
                case 'is not':
                    return t`Type to filter by "${partialBlock.parameter.label ?? partialBlock.parameter.field}"`;
            }
        }
        if (!partialBlock && selectedBlocks.length === 0) {
            return placeholder;
        }
        return undefined;
    };

    return (
        <>
            <ClickAwayListener
                onClickAway={() => {
                    setShowMenu(false);
                    dispatch({ type: 'removePartialFilter' });
                }}
            >
                <Container>
                    <SearchIcon size={16} color={colorSystem.neutral[7]} />
                    {blocks}
                    {state.partialBlock && <></>}
                    <ParametricInputTooltip
                        arrow={false}
                        open={Boolean(tooltip)}
                        placement="bottom-start"
                        title={tooltip}
                    >
                        <Input
                            placeholder={getPlaceholder()}
                            value={state.query}
                            autoFocus={autoFocus}
                            onChange={handleChange}
                            onKeyUp={handleKeyUp}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                if (e.key === 'Tab') {
                                    e.preventDefault();
                                } else if (e.key === 'Backspace') {
                                    dispatch({ type: 'backspace' });
                                }
                            }}
                            ref={state.inputRef}
                            onFocus={() => {
                                setShowMenu(true);
                            }}
                            onClick={() => {
                                setShowMenu(true);
                            }}
                            aria-label={placeholder}
                            // Fixes flaky chromatic test
                            aria-describedby={undefined}
                        />
                    </ParametricInputTooltip>
                    {showClearIcon && (
                        <IconButton
                            size="medium"
                            aria-label={t`Clear search`}
                            disabled={!showClearIcon}
                            onClick={() => {
                                dispatch({ type: 'clearSearch' });
                            }}
                            style={{
                                padding: 4,
                            }}
                        >
                            <XIcon />
                        </IconButton>
                    )}
                </Container>
            </ClickAwayListener>
        </>
    );
}

function findMenu<T, TAutocompleteState>({
    state,
    dispatch,
    autocompleteState,
    setShowMenu,
}: {
    state: ParametricSearchState<T, TAutocompleteState>;
    dispatch: React.Dispatch<Actions<T, TAutocompleteState>>;
    autocompleteState?: TAutocompleteState;
    setShowMenu: React.Dispatch<React.SetStateAction<boolean>>;
}): '' | JSX.Element {
    if (state.partialBlock?.selectedOp) {
        return (
            <MenuOperatorArgument
                autocompleteState={autocompleteState}
                state={state}
                onSelect={(parameter, label) => {
                    dispatch({ type: 'selectParameter', parameter, label });
                    setShowMenu(false);
                    dispatch({ type: 'removePartialFilter' });
                }}
                onCancel={() => {
                    dispatch({ type: 'removePartialFilter' });
                }}
                onSearch={() => dispatch({ type: 'submitQuery' })}
            />
        );
    }

    if (state.partialBlock?.parameter) {
        return (
            <MenuOperators
                inputRef={state.inputRef}
                query={state.query}
                partialFilter={state.partialBlock}
                onSelect={(operator) => {
                    dispatch({ type: 'selectOperator', operator });
                }}
                onSearch={() => dispatch({ type: 'submitQuery' })}
            />
        );
    }

    if (!state.partialBlock) {
        const allowedParameters = findAllowedParameters(state, autocompleteState);

        return (
            <MenuFields
                showIcons={!state.config.noIcons}
                inputRef={state.inputRef}
                parameters={allowedParameters}
                query={state.query}
                onSearch={() => dispatch({ type: 'submitQuery' })}
                onSelect={(selected) => {
                    dispatch({ type: 'selectField', selected, autocompleteState });
                }}
                showSearch={state.config.showSearch ?? true}
            />
        );
    }

    return '';
}

const DefaultContainer = styled('span')({
    display: 'inline-flex',
    padding: '4px 8px',
    alignItems: 'center',
    gap: 4,
    border: `1px solid ${colorSystem.neutral[3]}`,
    borderRadius: 4,
    width: '100%',
    minWidth: '280px',
    boxSizing: 'border-box',
    minHeight: 40,
    flexWrap: 'wrap',
    background: colorSystem.neutral.white,
    '&:focus-within': {
        border: `1px solid ${colorSystem.primary[5]}`,
        boxShadow: `0px 0px 0px 2px ${colorSystem.primary[3]}`,
    },
    '&:focus': {
        border: `1px solid ${colorSystem.primary[5]}`,
        boxShadow: `0px 0px 0px 2px ${colorSystem.primary[3]}`,
    },
    '&:hover': {
        border: `1px solid ${colorSystem.primary[5]}`,
    },
});

const DefaultInput = styled('input')({
    width: '10px',
    border: 'none',
    outline: 'none',
    flexGrow: 1,
    padding: 0,
    margin: 0,
    background: 'transparent',
    // Else the browser's `FieldText` color applies, which turns white under a forced `color-scheme: dark`.
    color: colorSystem.neutral[9],
    fontFamily: INTER,
    fontSize: 14,
    lineHeight: '24px',
    '&::placeholder': {
        opacity: 1,
        color: colorSystem.neutral[6],
    },
});

export const ParametricSearchInputDefaults = {
    DefaultContainer,
    DefaultInput,
    DefaultSearchBlockView,
};
